/**
 * Cell Batches List Page
 * Displays all cell batches with pagination, filtering, and status indicators.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useCellBatchesList, type CellBatchView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { canReadCellBatch } from '@/lib/labPermissions';

export default function CellBatchesListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch, error } = useCellBatchesList();

  const [batches, setBatches] = useState<CellBatchView[]>([]);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageLoading, setPageLoading] = useState(false);

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadCellBatch(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看批次追溯的权限</p>
        </Card>
      </div>
    );
  }

  // Load batches
  useEffect(() => {
    const loadBatches = async () => {
      setPageLoading(true);
      try {
        const statusValue = statusFilter as 'planned' | 'sample_collected' | 'sample_received' | 'processing' | 'qc_pending' | 'released' | 'infusion_scheduled' | 'infused' | 'archived' | 'exception' | undefined;
        const data = await fetch({ page, limit: 20, ...(statusFilter && { status: statusValue }) });
        if (data) {
          setBatches(data.items || []);
        }
      } catch (err) {
        toast.error(`加载批次失败: ${error?.message || '未知错误'}`);
      } finally {
        setPageLoading(false);
      }
    };

    loadBatches();
  }, [page, statusFilter, fetch, error]);

  const handleRowClick = (batchId: string) => {
    navigate(`/lab/batches/${batchId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">批次追溯</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Input
          placeholder="搜索批次号或患者名称"
          className="max-w-sm"
          disabled={pageLoading}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="rounded border border-input bg-background px-3 py-2 text-sm"
          disabled={pageLoading}
        >
          <option value="">所有状态</option>
          <option value="planned">计划中</option>
          <option value="sample_collected">样本已采集</option>
          <option value="sample_received">样本已接收</option>
          <option value="processing">处理中</option>
          <option value="qc_pending">质检待审</option>
          <option value="released">已放行</option>
          <option value="infusion_scheduled">回输已排期</option>
          <option value="infused">已回输</option>
          <option value="archived">已归档</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>

      {/* Batches Table */}
      <Card className="overflow-hidden">
        {pageLoading ? (
          <div className="flex min-h-96 items-center justify-center">
            <div className="text-sm text-muted-foreground">加载中…</div>
          </div>
        ) : batches.length === 0 ? (
          <div className="flex min-h-96 items-center justify-center">
            <div className="text-sm text-muted-foreground">暂无批次数据</div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">批次号</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">患者名称</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">状态</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">创建时间</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">操作</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr
                  key={batch.id}
                  className="border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(batch.id)}
                >
                  <td className="px-6 py-3 text-sm font-mono">{batch.batchNo}</td>
                  <td className="px-6 py-3 text-sm">{batch.customerId || batch.patientNameHash || 'N/A'}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-900">
                      {getStatusLabel(batch.status)}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm text-muted-foreground">
                    {new Date(batch.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/lab/batches/${batch.id}`);
                      }}
                    >
                      查看详情
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          第 {page} 页
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1 || pageLoading}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageLoading}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    planned: '计划中',
    sample_collected: '样本已采集',
    sample_received: '样本已接收',
    processing: '处理中',
    qc_pending: '质检待审',
    released: '已放行',
    infusion_scheduled: '回输已排期',
    infused: '已回输',
    archived: '已归档',
    cancelled: '已取消',
    exception_hold: '异常暂停',
    qc_failed: '质检失败',
  };
  return labels[status] || status;
}
